import { useEffect, useRef, useState } from 'react'
import {
	AtSign,
	Bug,
	Inbox,
	Lightbulb,
	Mail,
	MessageCircle,
	MessageSquare,
	Star,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { PlatformIcon } from '@/components/brand-mentions/PlatformIcon'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { cn } from '@/lib/utils'
import { BRAND_MENTION_PLATFORMS } from '@/types/brandMentions'

type CapabilityId =
	| 'bugs'
	| 'features'
	| 'reviews'
	| 'surveys'
	| 'liveChat'
	| 'contact'
	| 'mentions'

const CAPABILITIES: CapabilityId[] = [
	'bugs',
	'features',
	'reviews',
	'mentions',
	'surveys',
	'liveChat',
	'contact',
]

const NODE_STYLES: Record<
	CapabilityId,
	{ icon: typeof Bug; color: string; bg: string; ring: string }
> = {
	bugs: { icon: Bug, color: 'text-red-500', bg: 'bg-red-50', ring: 'ring-red-200' },
	features: { icon: Lightbulb, color: 'text-purple-500', bg: 'bg-purple-50', ring: 'ring-purple-200' },
	reviews: { icon: Star, color: 'text-emerald-500', bg: 'bg-emerald-50', ring: 'ring-emerald-200' },
	surveys: { icon: MessageSquare, color: 'text-blue-500', bg: 'bg-blue-50', ring: 'ring-blue-200' },
	liveChat: { icon: MessageCircle, color: 'text-orange-500', bg: 'bg-orange-50', ring: 'ring-orange-200' },
	contact: { icon: Mail, color: 'text-teal-500', bg: 'bg-teal-50', ring: 'ring-teal-200' },
	mentions: { icon: AtSign, color: 'text-sky-500', bg: 'bg-sky-50', ring: 'ring-sky-200' },
}

function polarOffset(index: number, radius: number) {
	const angle = (index / CAPABILITIES.length) * Math.PI * 2 - Math.PI / 2
	return {
		x: Math.cos(angle) * radius,
		y: Math.sin(angle) * radius,
	}
}

function labelSide(index: number): 'top' | 'right' | 'bottom' | 'left' {
	const deg = (((index / CAPABILITIES.length) * 360 - 90) % 360 + 360) % 360
	if (deg > 315 || deg <= 45) return 'right'
	if (deg <= 135) return 'bottom'
	if (deg <= 225) return 'left'
	return 'top'
}

export function HeroCapabilityAnimation() {
	const { t } = useTranslation()
	const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
	const isDesktop = useMediaQuery('(min-width: 1024px)')
	const boxRef = useRef<HTMLDivElement>(null)
	const [radius, setRadius] = useState(120)
	const [activeIndex, setActiveIndex] = useState(0)
	const [flyKey, setFlyKey] = useState(0)

	useEffect(() => {
		const el = boxRef.current
		if (!el) return
		const update = () => setRadius(el.clientWidth * (isDesktop ? 0.35 : 0.32))
		update()
		const observer = new ResizeObserver(update)
		observer.observe(el)
		return () => observer.disconnect()
	}, [isDesktop])

	useEffect(() => {
		if (reducedMotion) return
		const timer = window.setInterval(() => {
			setActiveIndex((current) => (current + 1) % CAPABILITIES.length)
			setFlyKey((key) => key + 1)
		}, 2200)
		return () => window.clearInterval(timer)
	}, [reducedMotion])

	const activeId = CAPABILITIES[activeIndex]
	const activeOffset = polarOffset(activeIndex, radius)

	return (
		<div className="mx-auto flex w-full max-w-[22rem] flex-col items-center lg:max-w-[28rem]">
			<div ref={boxRef} className="relative aspect-square w-full overflow-visible">
				<div className="absolute left-1/2 top-1/2 z-10 flex h-[5.75rem] w-[5.75rem] -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center rounded-2xl border border-primary/20 bg-white shadow-lg sm:h-[6.25rem] sm:w-[6.25rem]">
					<Inbox className="h-6 w-6 text-primary sm:h-7 sm:w-7" />
					<p className="mt-1 text-sm font-semibold text-foreground">
						{t('landing.heroCycle.inbox')}
					</p>
					{!reducedMotion && (
						<span
							key={flyKey}
							className="pointer-events-none absolute left-1/2 top-1/2 z-20 h-2.5 w-2.5 rounded-full bg-primary animate-hero-cap-fly"
							style={{
								['--hero-cap-x' as string]: `${activeOffset.x}px`,
								['--hero-cap-y' as string]: `${activeOffset.y}px`,
							}}
						/>
					)}
				</div>
				{CAPABILITIES.map((id, index) => {
					const isActive = reducedMotion || index === activeIndex
					const { icon: Icon, color, bg, ring } = NODE_STYLES[id]
					const { x, y } = polarOffset(index, radius)
					const orbitSide = labelSide(index)
					const side =
						!isDesktop && (orbitSide === 'left' || orbitSide === 'right')
							? 'bottom'
							: orbitSide
					return (
						<div
							key={id}
							className="absolute left-1/2 top-1/2"
							style={{ transform: `translate(-50%, -50%) translate(${x}px, ${y}px)` }}
						>
							<div
								className={cn(
									'relative flex h-10 w-10 items-center justify-center rounded-full border bg-white shadow-sm transition-all duration-300 sm:h-11 sm:w-11',
									bg,
									isActive ? `scale-110 ring-4 ${ring}` : 'scale-100 border-border/60 opacity-70'
								)}
							>
								<Icon className={cn('h-4 w-4 sm:h-5 sm:w-5', color)} />
								<p
									className={cn(
										'absolute w-[4.75rem] text-[11px] font-medium leading-tight sm:w-[5.4rem] sm:text-[12px]',
										side === 'top' && 'bottom-[calc(100%+0.35rem)] left-1/2 -translate-x-1/2 text-center',
										side === 'bottom' && 'top-[calc(100%+0.35rem)] left-1/2 -translate-x-1/2 text-center',
										side === 'left' && 'right-[calc(100%+0.45rem)] top-1/2 -translate-y-1/2 text-right',
										side === 'right' && 'left-[calc(100%+0.45rem)] top-1/2 -translate-y-1/2 text-left',
										isActive ? 'text-foreground' : 'text-muted-foreground'
									)}
								>
									{t(`landing.heroCycle.${id}`)}
								</p>
							</div>
						</div>
					)
				})}
			</div>
			<div className="mt-1 min-h-[2.75rem] text-center">
				<p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
					{t('landing.heroCycle.prefix')}
				</p>
				<span
					className={cn(
						'mt-2 flex items-center justify-center gap-1.5',
						activeId === 'mentions' ? 'opacity-100' : 'opacity-0'
					)}
				>
					{BRAND_MENTION_PLATFORMS.map((platform) => (
						<PlatformIcon
							key={platform}
							platform={platform}
							className="h-3.5 w-3.5"
							colored
						/>
					))}
				</span>
			</div>
		</div>
	)
}
